import React, { useEffect, useState } from "react";
import { getChatSession } from "@/lib/api";

/**
 * ChatHistoryPanel — Renders the result of list_sessions, recall_chats, or
 * open_session voice actions. Lets the owner browse/load past conversations.
 */
export default function ChatHistoryPanel({ data, mode }) {
  // mode: "sessions" | "recall" | "session"
  if (!data) return null;

  if (mode === "session") return <SessionTranscript data={data} />;
  if (mode === "recall")  return <RecallResults data={data} />;
  return <SessionsList data={data} />;
}

function SessionsList({ data }) {
  const [openSid, setOpenSid] = useState(null);
  const [loaded, setLoaded] = useState(null);
  const sessions = data.sessions || [];

  useEffect(() => {
    if (!openSid) return;
    setLoaded(null);
    getChatSession(openSid).then(setLoaded).catch(() => {});
  }, [openSid]);

  return (
    <div data-testid="chat-sessions">
      {!openSid && (
        <>
          <div className="rc-label text-[10px] mb-2 opacity-70">{sessions.length} CONVERSATIONS · MOST RECENT FIRST</div>
          <div className="space-y-1.5 max-h-[65vh] overflow-y-auto pr-1">
            {sessions.map((s, i) => (
              <button
                key={s.session_id || i}
                onClick={() => setOpenSid(s.session_id)}
                className="w-full text-left border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] px-3 py-2.5 transition-all"
                data-testid={`session-row-${i}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--rc-text)] truncate">{s.first_message || "(no opener)"}</div>
                    <div className="rc-label text-[9px] opacity-60 mt-1">
                      {(s.last_active || "").slice(0, 16).replace("T", " · ")} · {s.turn_count} turns
                    </div>
                  </div>
                  <div className="rc-label text-[9px] opacity-50 ml-2 shrink-0">{s.session_id?.slice(0, 6)}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
      {openSid && (
        <>
          <button onClick={() => { setOpenSid(null); setLoaded(null); }} className="rc-label text-[10px] mb-2 opacity-70 hover:rc-glow-text" data-testid="session-back">
            ← BACK TO LIST
          </button>
          {!loaded ? <div className="rc-label text-[10px] opacity-60">Loading…</div> : <SessionTranscript data={loaded} />}
        </>
      )}
    </div>
  );
}

function SessionTranscript({ data }) {
  const turns = data.turns || [];
  return (
    <div data-testid="chat-transcript">
      <div className="rc-label text-[10px] mb-2 opacity-70">
        {turns.length} TURNS · STARTED {(data.meta?.started_at || data.started_at || "").slice(0, 16).replace("T", " · ")}
      </div>
      <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
        {turns.map((t, i) => (
          <div key={t.id || i} className={`px-3 py-2 border ${t.role === "user" ? "border-[var(--rc-gold-dim)]" : "border-[var(--rc-gold)] rc-glow-text"}`} data-testid={`turn-${i}`}>
            <div className="rc-label text-[9px] opacity-60 mb-1">
              {t.role === "user" ? "YOU" : "RAW"} · {(t.created_at || "").slice(11, 16)}
            </div>
            <div className="text-xs text-[var(--rc-text)]">{t.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecallResults({ data }) {
  return (
    <div data-testid="chat-recall">
      <div className="rc-label text-[10px] mb-3 opacity-70">
        QUERY · "{data.query}" · {data.match_count} MATCHES IN {data.sessions} CONVERSATIONS
      </div>
      <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
        {(data.groups || []).map((g, i) => (
          <div key={g.session_id || i} className="border border-[var(--rc-gold-dim)] p-3" data-testid={`recall-group-${i}`}>
            <div className="flex justify-between items-center mb-2">
              <div className="rc-label text-[9px] opacity-70">
                {(g.last_match_at || "").slice(0, 16).replace("T", " · ")} · {g.match_count} matches
              </div>
              <div className="rc-label text-[9px] opacity-50">{g.session_id?.slice(0, 8)}</div>
            </div>
            <div className="space-y-1.5">
              {(g.preview || []).map((p, j) => (
                <div key={j} className="border-l-2 border-[var(--rc-gold-dim)] pl-2 py-0.5">
                  <div className="rc-label text-[8px] opacity-60">{p.role === "user" ? "YOU" : "RAW"} · {p.when}</div>
                  <div className="text-[11px] text-[var(--rc-text)]">{p.text}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
